/**
 * src/features/printing/hooks/useTenantPrinters.ts
 *
 * Liste les imprimantes configurées du tenant courant, filtrées par types.
 * Utilisé par KitchenPrinterPickerDialog (PR-3).
 *
 * Invariants :
 *   4.B.2 — pas de supabase.* direct : passe par PrintingApi.listTenantPrinters
 *   4.A.10 — tenant_id résolu depuis le JWT via useEffectiveTenantId
 *   4.B.4 — 0 any
 */

import { useQuery } from '@tanstack/react-query';
import { errOf } from '@/api/result';
import { useEffectiveTenantId } from '@/hooks/useTenant';
import { PrintingApi } from '@/api/printing';
import type { PrinterDevice, PrinterType } from '@/api/printing';

export type UseTenantPrintersOptions = {
  /** Filtrage par printer_type. Si omis : toutes les imprimantes actives. */
  types?: PrinterType[];
  /** Désactive la query (utile si tenantId pas encore chargé). */
  enabled?: boolean;
};

export type UseTenantPrintersResult = {
  printers: PrinterDevice[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  /** True si la query est résolue ET qu'il n'y a aucune imprimante configurée. */
  isEmpty: boolean;
  refetch: () => void;
};

export function useTenantPrinters(options: UseTenantPrintersOptions = {}): UseTenantPrintersResult {
  const tenantId = useEffectiveTenantId();
  const { types, enabled = true } = options;

  // Clé stable : types triés pour éviter les caches dupliqués sur ordre différent.
  const typesKey = types ? [...types].sort().join(',') : 'all';

  const query = useQuery({
    queryKey: ['tenant_printers', tenantId, typesKey],
    queryFn: async (): Promise<PrinterDevice[]> => {
      if (!tenantId) return [];
      const result = await PrintingApi.listTenantPrinters(tenantId, types);
      if (!result.ok) {
        const e = errOf(result);
        throw new Error(e.type === 'invalid_input' ? e.message : 'Erreur chargement imprimantes');
      }
      return result.value;
    },
    enabled: Boolean(tenantId) && enabled,
    staleTime: 30_000, // imprimantes changent rarement
  });

  return {
    printers: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    isEmpty: !query.isLoading && (query.data?.length ?? 0) === 0,
    refetch: () => {
      void query.refetch();
    },
  };
}
